<template>
  <component v-if="showTab" :is="tag" :class="['collection-tabs', {'with-tabs': hasTabs}]">
    <div v-if="blok.title || subtitleRte" id="tablist-1" class="header">
      <h2 v-if="blok.title" class="size-m" v-html="blok.title"></h2>
      <div v-if="subtitleRte" class="size-s" v-html="subtitleRte"></div>
    </div>
    <div v-if="hasTabs" ref="manual" role="tablist" aria-labelledby="tablist-1" class="manual parent-bg">
      <button
        v-if="hasScroll && (current > 0 || scrolling)"
        class="arrow left arrow-mobile-only"
        :title="useMicroCopy('collectiontabs.arrowleft.title')"
        @click="scrollToTablistItem('prev')"
      >
        <SvgIcon name="func_left" />
      </button>
      <div class="tabs-desktop-only">
        <p v-if="blok.summary_title">{{ blok.summary_title }}</p>
        <div
          v-for="(item, index) in blok.items"
          :id="`nav_tabpanel-${index}`"
          :key="item._uid"
          class="tab"
          :class="{ 'is-selected': index === activeIndex }"
        >
          <button
            :id="`tab-${index}`"
            type="button"
            role="tab"
            :aria-selected="index === activeIndex"
            :aria-controls="`tabpanel-${index}`"
            @click="onTabActivate(index)"
          >
            <span class="focus size-xl">
              {{ formatTabTitle(item.title) }}
            </span>
          </button>
          <p v-if="item.subtitle" class="size-xxs minor">
            {{ item.subtitle }}
          </p>
        </div>
      </div>
      <div
        class="tablist-scroll tabs-mobile-only"
        :class="isOpened ? 'is-opened' : ''"
        @scroll="onTablistScroll()"
        @click="toggleMenu()"
      >
        <div
          v-for="(item, index) in blok.items"
          :id="`nav_tabpanel-${index}`"
          :key="item._uid"
          :class="[
            'tab',
            {
              'current-item': index === current,
              'is-selected': index === activeIndex,
            },
          ]"
          @click="scrollToTablistItem()"
        >
          <button
            :id="`tab-${index}`"
            type="button"
            role="tab"
            :aria-selected="index === activeIndex"
            :aria-controls="`tabpanel-${index}`"
            @click="onTabActivate(index)"
          >
            <span class="focus size-xl">
              {{ formatTabTitle(item.title) }}
            </span>
          </button>
        </div>
      </div>
      <button
        v-if="hasScroll && (current < blok.items.length - 1 || scrolling)"
        class="arrow right arrow-mobile-only"
        :title="useMicroCopy('collectiontabs.arrowright.title')"
        @click="scrollToTablistItem('next')"
      >
        <SvgIcon name="func_right" />
      </button>
    </div>
    <div :class="['collection-tabs-container', {'with-tabs': hasTabs}]">
      <template v-for="(item, index) in blok.items" :key="item._uid">
        <CollectionPanel :index="index" :blok="item" />
      </template>
    </div>
  </component>
</template>

<script setup>
import { useResizeObserver } from "@vueuse/core";
import TabsManual from "../utils/tabs";

const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  tag: {
    type: String,
    required: false,
    default: "div",
  },
});
const { blok } = toRefs(props);
const isString =
  typeof blok.value.subtitle === "string" ||
  blok.value.subtitle instanceof String;
const subtitleRte = isString
  ? blok.value.subtitle
  : useRenderRichText(blok.value.subtitle);
let tablistScroll = null;
let scrolling = ref(false);
let hasScroll = ref(false);
let current = ref(0);
const activeIndex = ref(0);
let allTabs = null;
let firstElt = null;
let lastElt = null;
const SIDE_MENU_QUERY = "(min-width: 900px)";
let ticking = false;
let programmaticScroll = false;
let settleTimer = null;

onMounted(() => {
  initTabs();
  if (hasTabs) {
    window.addEventListener("scroll", onWindowScroll, { passive: true });
  }

  setTimeout(() => {
    window.scrollTo(0, 0);
    syncActiveTab();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onWindowScroll);
  clearTimeout(settleTimer);
});

const config = useRuntimeConfig();
const country = config.public["country"];

const formatTabTitle = (title) => {
  return country == "es" ? title[0].toUpperCase() + title.slice(1) : title;
};

const checkHasScroll = () => {
  const scrollWidth = tablistScroll.scrollWidth;
  const windowWidth = window.innerWidth;
  return scrollWidth > windowWidth;
};

const hasTabs = blok.value.items.length > 1;
const manual = ref(null);
useResizeObserver(manual, () => {
  initTabs();
});
const showTab = blok.value.items.length > 0;

const initTabs = () => {
  const tablists = document.querySelectorAll("[role=tablist].manual");
  for (var i = 0; i < tablists?.length; i++) {
    const tablist = tablists[i];
    if (tablist.dataset.tabsBound === "true") continue;
    new TabsManual(tablist);
    tablist.dataset.tabsBound = "true";
  }
  tablistScroll = manual.value?.querySelector(".tablist-scroll") ?? null;
  if (tablistScroll) {
    hasScroll.value = checkHasScroll();
    allTabs = tablistScroll?.querySelectorAll(".tab");
    firstElt = allTabs[0];
    lastElt = allTabs[allTabs.length - 1];
  }
};

const scrollToTablistItem = (direction) => {
  if (!tablistScroll) return;
  let siblingElt;
  if (direction?.length) {
    current.value =
      direction === "next" ? current.value + 1 : current.value - 1;
    const currentElt = tablistScroll.querySelector(".current-item");
    siblingElt =
      direction === "next"
        ? currentElt?.nextElementSibling
        : currentElt?.previousElementSibling;
  } else {
    siblingElt =
      tablistScroll.querySelector(".is-selected") ??
      tablistScroll.querySelector(".current-item");
  }
  if (!siblingElt) return;
  tablistScroll.scrollTo({
    top: siblingElt.offsetTop,
    left: siblingElt.offsetLeft - 10,
    behavior: "smooth",
  });
};

const onTablistScroll = () => {
  if (manual.value?.closest(".has-smaller-tabs-nav")) return;
  setTimeout(() => {
    if (isInView(firstElt)) {
      current.value = 0;
      scrolling.value = false;
    } else if (isInView(lastElt)) {
      current.value = allTabs.length - 1;
      scrolling.value = false;
    } else {
      scrolling.value = true;
    }
  }, 400);
};

const isInView = (elt) => {
  const elementRect = elt.getBoundingClientRect();
  const parentRect = elt.parentNode.getBoundingClientRect();
  return (
    elementRect.right >= parentRect.left &&
    elementRect.left + elementRect.width <= parentRect.right
  );
};

let isOpened = ref(false);
const toggleMenu = () => {
  isOpened.value = !isOpened.value;
};

const getRootElement = () =>
  manual.value?.closest(".collection-tabs") ?? null;

const getReadingOffset = () => {
  const headerBottom = Math.max(
    0,
    document.querySelector(".global-header")?.getBoundingClientRect().bottom ??
      0
  );
  const isSideMenu = window.matchMedia(SIDE_MENU_QUERY).matches;
  if (!isSideMenu && manual.value) {
    const menuRect = manual.value.getBoundingClientRect();
    const menuIsStuck = menuRect.top <= headerBottom + 1;
    return (menuIsStuck ? menuRect.bottom : headerBottom) + 16;
  }
  return Math.max(headerBottom + 48, 120);
};

const revealActiveTab = (index) => {
  if (!window.matchMedia(SIDE_MENU_QUERY).matches) return;
  const menu = manual.value;
  if (!menu) return;
  const item = menu.querySelectorAll(".tabs-desktop-only .tab")[index];
  if (!item) return;

  const menuRect = menu.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const isVisible =
    itemRect.top >= menuRect.top + 8 && itemRect.bottom <= menuRect.bottom - 8;
  if (isVisible) return;

  const top =
    itemRect.top - menuRect.top + menu.scrollTop - menu.clientHeight / 3;
  menu.scrollTo({
    top: Math.max(0, top),
    behavior: "smooth",
  });
};

const syncActiveTab = () => {
  const root = getRootElement();
  if (!root?.closest(".has-smaller-tabs-nav")) return;

  const panels = root.querySelectorAll(
    ".collection-tabs-container > [role='tabpanel']"
  );
  if (!panels.length) return;

  const marker = getReadingOffset();
  let nextIndex = 0;
  panels.forEach((panel, index) => {
    if (panel.getBoundingClientRect().top <= marker) nextIndex = index;
  });

  const scrolledToEnd =
    window.scrollY > 0 &&
    window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;
  if (scrolledToEnd) nextIndex = panels.length - 1;

  if (nextIndex === activeIndex.value) return;
  activeIndex.value = nextIndex;
  current.value = nextIndex;
  revealActiveTab(nextIndex);
};

const releaseProgrammaticScroll = () => {
  programmaticScroll = false;
  syncActiveTab();
};

const onWindowScroll = () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    ticking = false;
    if (programmaticScroll) {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(releaseProgrammaticScroll, 180);
      return;
    }
    syncActiveTab();
  });
};

const onTabActivate = (index) => {
  activeIndex.value = index;
  current.value = index;
  revealActiveTab(index);
  programmaticScroll = true;
  clearTimeout(settleTimer);
  settleTimer = setTimeout(releaseProgrammaticScroll, 1000);
};
</script>

<style lang="scss">
.background-collection-tabs {
  --parent-bg: var(--solid-07);

  &.alternate {
    background: var(--solid-01);
    --parent-bg: var(--solid-01);

    .arrow.left {
      background: linear-gradient(
        270deg,
        rgba(255, 255, 255, 0) 35.42%,
        var(--solid-01) 100%
      );
    }
    .arrow.right {
      background: linear-gradient(
        270deg,
        var(--solid-01) 35.42%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }
}
.header-facility .collection-tabs .manual {
  @include for-tablet-landscape-up {
    top: 160px !important;
  }
}
</style>

<style lang="scss" scoped>
.collection-tabs {
  position: relative;

  @include for-tablet-landscape-up {
    grid-template-rows: min-content min-content auto;
  }

  &.with-tabs {
    min-height: 100vh;
  }

  .header {
    grid-column: 2 / -2;
    grid-row: 1 / 2;
    margin-bottom: 2.5rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    @include for-tablet-landscape-up {
      max-width: 56.25rem;
      margin: 0 auto 6.25rem;
    }
  }

  .manual {
    position: sticky;
    z-index: 10;
    display: flex;
    top: 0;
    grid-column: 2/-2;
    background: transparent !important;
    @include pair-7;

    @include for-tablet-landscape-up {
      grid-row: 2 / 2;
      grid-column: 2 / span 6;
      height: calc(100vh - 3.5vw);
      flex-direction: column;
      overflow-y: auto;
      gap: 1.25rem;
      top: 69px;
      background: transparent;

      & > div {
        flex-grow: 0.001;
        transition: flex-grow 0.3s;
      }
    }

    [role="tab"] {
      white-space: nowrap;
      @include for-tablet-landscape-up {
        white-space: inherit;
      }
    }

    [role="tab"],
    [role="tab"]:focus,
    [role="tab"]:hover {
      display: inline-block;
      position: relative;
      z-index: 2;
      background: none;
      outline: none;
      font-weight: bold;
      overflow: hidden;
      text-align: left;
      cursor: pointer;
      appearance: none;
      border: none;
      padding: 0;
      color: var(--text-100);
    }

    [role="tab"][aria-selected="true"] {
      text-decoration: underline;
      text-underline-offset: 0.25rem;
      @include for-tablet-landscape-up {
        text-decoration: none;
      }
    }
    @include for-tablet-landscape-up {
      [role="tab"][aria-selected="true"] {
        &:before {
          content: ">";
          font-size: 2rem;
          vertical-align: super;
        }
      }
    }

    [role="tab"][aria-selected="false"] {
      opacity: 0.5;
      & + .minor {
        display: none;
      }
    }

    [role="tab"] span.focus {
      line-height: 125%;
      @include for-tablet-landscape-up {
        width: 90%;
      }
    }

    .minor {
      opacity: 0.7;
    }
  }

  .collection-tabs-container {
    grid-column: 2 / -2;
    overflow: auto;
    @include for-desktop-up {
      grid-column: 5 / -5;
    }
    &.with-tabs {
      @include for-tablet-landscape-up {
        grid-column: 8 / -2;
        grid-row: 2 /2;
      }
    }
  }

  .collection-tabs-item {
    visibility: visible;
    height: auto;
    opacity: 1;
    transition: opacity 1.2s linear;

    &.is-hidden {
      height: 0;
      padding-top: 0;
      overflow: hidden;
      visibility: hidden;
      opacity: 0;
      transition: visibility 0s 1.2s, opacity 1.2s linear;
    }
  }

  .with-tabs .collection-tabs-item {
    padding-top: 1.5rem;

    @include for-tablet-landscape-up {
      padding-top: 0;
    }
  }
}

.tablist-scroll {
  display: flex;
  gap: 0 1.5rem;
  padding-top: 0.75rem;
  padding-bottom: 0.5rem;
  padding-right: 2rem;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
    width: 0 !important;
  }
}
.tabs-desktop-only {
  display: none;
  @include for-tablet-landscape-up {
    display: contents;
  }
}

.tabs-mobile-only {
  display: flex;
  @include for-tablet-landscape-up {
    display: none !important;
  }
}

.arrow {
  border: 0;
  padding: 0;
  cursor: pointer;
  position: absolute;
  z-index: 100;
  top: 50%;
  transform: translateY(-50%);
  margin-top: 0.2rem;
  width: 3.25rem;
  height: 3.25rem;
  display: flex;
  align-items: center;
  &-mobile-only {
    @include for-tablet-landscape-up {
      display: none;
    }
  }
  &.left {
    justify-content: flex-start;
    left: -0.4rem;
    background: linear-gradient(
      270deg,
      rgba(255, 255, 255, 0) 35.42%,
      var(--solid-07) 100%
    );
  }
  &.right {
    justify-content: flex-end;
    right: -0.4rem;
    background: linear-gradient(
      270deg,
      var(--solid-07) 35.42%,
      rgba(255, 255, 255, 0) 100%
    );
  }
}
</style>
